// src/components/booking/RescheduleModal.jsx
import { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebase";
import { fetchBookedSlots } from "../../hooks/useBooking";
import DateTimeStep from "./DateTimeStep";
import { X, Loader2, CalendarCheck } from "lucide-react";
import { formatDateShort, toDateStrInTz } from "../../utils/dateUtils";
import { useTenant } from "../../context/TenantContext";

export default function RescheduleModal({ booking, onClose, onSuccess }) {
  const { tenant } = useTenant();
  const [selectedDate,  setSelectedDate]  = useState(null);
  const [selectedTime,  setSelectedTime]  = useState(null);
  const [bookedSlots,   setBookedSlots]   = useState([]);
  const [loadingSlots,  setLoadingSlots]  = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState("");

  // Fetch availability when date changes
  useEffect(() => {
    if (!selectedDate) { setBookedSlots([]); return; }
    setLoadingSlots(true);
    const tz = tenant?.timezone || "America/New_York";
    fetchBookedSlots(
      booking.barber.id,
      toDateStrInTz(selectedDate, tz),
      booking.id          // exclude current booking so its slot is available
    )
      .then(setBookedSlots)
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, booking.barber.id, booking.id]);

  async function handleConfirm() {
    if (!selectedDate || !selectedTime) return;
    setSaving(true);
    setError("");
    try {
      const tz         = tenant?.timezone || "America/New_York";
      const newDateStr = toDateStrInTz(selectedDate, tz);

      // Server-side: validates business hours, swaps slot locks atomically,
      // updates the booking, and emails the customer.
      const fn = httpsCallable(functions, "rescheduleBooking");
      const { data } = await fn({
        bookingId:  booking.id,
        newDateStr,
        newTime:    selectedTime,
      });

      onSuccess({ date: data.date, dateStr: data.dateStr, time: data.time });
    } catch (err) {
      const msg = (err?.message || "") + (err?.details || "");
      if (msg.includes("SLOT_TAKEN")) {
        setError("This time slot was just taken. Please choose a different time.");
      } else if (msg.includes("CLOSED_DAY")) {
        setError("The business is closed on the selected day. Please choose an open day.");
      } else if (msg.includes("OUTSIDE_HOURS")) {
        setError("That time is outside the business's opening hours.");
      } else {
        console.error("Reschedule error:", err);
        setError("Failed to reschedule. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  const isChanged = selectedDate && selectedTime;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto animate-fade-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-brand-100 flex items-center justify-center">
              <CalendarCheck size={17} className="text-brand-600" />
            </div>
            <div>
              <h2 className="font-semibold text-ink-900 text-lg leading-tight">Reschedule Appointment</h2>
              <p className="text-ink-400 text-xs font-sans">
                {booking.service?.name} · with {booking.barber?.name}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-ink-400
                       hover:bg-surface-100 hover:text-ink-700 transition-all">
            <X size={17} />
          </button>
        </div>

        {/* Current booking info */}
        <div className="mx-6 mt-5 px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl
                        flex items-center gap-2 text-xs text-ink-500 font-mono">
          <span className="text-ink-400">Current slot:</span>
          <span className="font-semibold text-ink-700">
            {booking.date ? formatDateShort(new Date(booking.date)) : "—"} · {booking.time}
          </span>
          {isChanged && (
            <>
              <span className="text-ink-300 mx-1">→</span>
              <span className="font-semibold text-brand-600">
                {formatDateShort(selectedDate)} · {selectedTime}
              </span>
            </>
          )}
        </div>

        {/* Date/time picker */}
        <div className="px-6 py-5">
          <DateTimeStep
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateSelect={d => { setSelectedDate(d); setSelectedTime(null); }}
            onTimeSelect={setSelectedTime}
            bookedSlots={bookedSlots}
            loadingSlots={loadingSlots}
            timezone={tenant?.timezone || "America/New_York"}
            businessHours={tenant?.businessHours || null}
            barberAvailability={booking.barber?.availability || null}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl
                          text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-surface-100">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={!isChanged || saving}
            className="btn-primary text-sm min-w-[170px] flex items-center justify-center gap-2">
            {saving
              ? <><Loader2 size={15} className="animate-spin" /> Rescheduling…</>
              : <><CalendarCheck size={15} /> Confirm Reschedule</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
